import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';
import { css, cx, keyframes } from '@emotion/css';

import { Link } from '#components/common/Link';
import { esIdiomaDeRumbo } from '#i18n';

import { BrujulaAnimada, DURACION_BRUJULA } from './BrujulaAnimada';
import { ATARDECER, CIELO, PuertoAtardecer } from './PuertoAtardecer';
import { SelectorIdioma } from './SelectorIdioma';

const CLAVE_VISTA = 'rumbo-inicio-visto';

/**
 * La primera vez la animación dura entera; las siguientes, en un tercio del
 * tiempo (nadie quiere esperar cinco segundos cada vez que entra).
 */
export function escalaAnimacion(yaVista: boolean): number {
  return yaVista ? 0.3 : 1;
}

function leerVista(): boolean {
  try {
    return window.localStorage.getItem(CLAVE_VISTA) === '1';
  } catch {
    return false;
  }
}

function marcarVista() {
  try {
    window.localStorage.setItem(CLAVE_VISTA, '1');
  } catch {
    // Sin almacenamiento (modo privado): la próxima vez se verá entera.
  }
}

type InicioRumboProps = {
  onContinuar: () => void | Promise<void>;
  /** Mensaje de error del inicio de sesión, si lo hay. */
  error?: ReactNode;
  /** Solo si este móvil tiene la huella activada. */
  onHuella?: () => void;
  /** Elige y guarda el idioma de este móvil. */
  onIdioma: (idioma: string) => void | Promise<void>;
};

/**
 * Pantalla de entrada de Rumbo (30-oct-2026, tema claro): el puerto de
 * Llafranc al atardecer arriba y una hoja de papel abajo con la brújula del
 * logo que se monta, el idioma y «Continuar con Google». Sin servidor que
 * cambiar: Rumbo tiene uno solo.
 */
export function InicioRumbo({
  onContinuar,
  error,
  onHuella,
  onIdioma,
}: InicioRumboProps) {
  const { t, i18n } = useTranslation();
  const idioma = i18n.resolvedLanguage;
  const [escala] = useState(() => escalaAnimacion(leerVista()));
  const [enviando, setEnviando] = useState(false);
  const s = (ms: number) => `${Math.round(ms * escala)}ms`;
  const tras = DURACION_BRUJULA - 700;

  useEffect(() => {
    marcarVista();
  }, []);

  async function continuar() {
    if (enviando) {
      return;
    }
    setEnviando(true);
    try {
      // Quien entra sin tocar el selector también «elige» el que está viendo,
      // para que Ajustes lo muestre y no «Predeterminado del sistema».
      if (idioma && esIdiomaDeRumbo(idioma)) {
        await onIdioma(idioma);
      }
      await onContinuar();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <View
      data-testid="inicio-rumbo"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 6000,
        background: CIELO,
        color: ATARDECER.tinta,
        overflow: 'hidden',
        alignItems: 'center',
      }}
    >
      <View
        style={{
          position: 'relative',
          flex: 1,
          width: '100%',
          maxWidth: 480,
          justifyContent: 'flex-end',
        }}
      >
        <View style={{ flex: 1, minHeight: 150, justifyContent: 'flex-end' }}>
          <PuertoAtardecer style={{ height: '100%', minHeight: 150 }} />
        </View>
        <Hoja>
          <View
            className={entra(s(tras), s(800))}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
          >
            <View
              aria-hidden
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: ATARDECER.icono,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BrujulaAnimada size={32} escala={escala} />
            </View>
            <h1
              style={{
                margin: 0,
                fontFamily: SERIF,
                fontSize: 30,
                fontWeight: 700,
                color: ATARDECER.tinta,
              }}
            >
              <Trans>Rumbo</Trans>
            </h1>
          </View>
          <span
            className={entra(s(tras + 200), s(800))}
            style={{
              marginTop: 6,
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontSize: 15,
              color: ATARDECER.tinta2,
            }}
          >
            {t('Your money, your decisions, your course.')}
          </span>
          <SelectorIdioma activo={idioma} onElegir={i => void onIdioma(i)} />
          <button
            type="button"
            data-testid="continuar-google"
            onClick={() => void continuar()}
            disabled={enviando}
            className={css([
              estiloBoton,
              {
                marginTop: 16,
                gap: 12,
                background: '#fff',
                color: '#1f1f1f',
                boxShadow: `0 2px 10px rgba(20,46,53,.18), inset 0 0 0 1px ${ATARDECER.linea}`,
                ':disabled': { opacity: 0.75, cursor: 'wait' },
              },
              entraEstilo(s(tras + 400), s(800)),
            ])}
          >
            <LogoGoogle />
            {t('Continue with Google')}
          </button>
          {onHuella && (
            <BotonSecundario
              testid="entrar-huella"
              onClick={onHuella}
              className={entra(s(tras + 600), s(800))}
            >
              <Trans>Sign in with fingerprint</Trans>
            </BotonSecundario>
          )}
          {/* Beta (G5): antes de entrar, que se sepa que es una prueba. */}
          <span
            data-testid="aviso-prueba-inicio"
            className={entra(s(tras + 600), s(800))}
            style={{
              marginTop: 14,
              fontSize: 13,
              fontWeight: 700,
              color: ATARDECER.latonOscuro,
            }}
          >
            <Trans>Trial version: use made-up data.</Trans>
          </span>
          {error && (
            <span
              role="alert"
              style={{
                marginTop: 12,
                color: '#a63a2b',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {error}
            </span>
          )}
          <p
            className={entra(s(tras + 700), s(800))}
            style={{
              margin: '18px 0 0',
              fontSize: 12,
              color: ATARDECER.tinta3,
            }}
          >
            <Trans
              i18nKey="By continuing you accept the <terms>terms</terms> and the <privacy>privacy policy</privacy>."
              components={{
                terms: (
                  <Link
                    variant="external"
                    linkColor="muted"
                    to="/condiciones/"
                  />
                ),
                privacy: (
                  <Link
                    variant="external"
                    linkColor="muted"
                    to="/privacidad/"
                  />
                ),
              }}
            />
          </p>
        </Hoja>
      </View>
    </View>
  );
}

/**
 * La hoja de papel de abajo, sobre el mar: lo que se lee y se toca. También
 * la usa la lista de espera.
 */
export function Hoja({
  children,
  alinear = 'center',
}: {
  children: ReactNode;
  alinear?: 'center' | 'stretch';
}) {
  return (
    <View
      style={{
        position: 'relative',
        marginTop: -26,
        background: ATARDECER.papel,
        borderRadius: '26px 26px 0 0',
        padding: '22px 24px calc(20px + env(safe-area-inset-bottom))',
        alignItems: alinear,
        textAlign: alinear === 'center' ? 'center' : 'left',
        boxShadow: '0 -10px 24px -16px rgba(20,46,53,.5)',
        flexShrink: 0,
      }}
    >
      {children}
    </View>
  );
}

const SERIF = 'Fraunces, Georgia, "Times New Roman", serif';

const estiloBoton = {
  width: '100%',
  maxWidth: 360,
  height: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 0,
  borderRadius: 999,
  fontSize: 15,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'transform 120ms ease',
  ':active': { transform: 'scale(.97)' },
  ':focus-visible': {
    outline: `2px solid ${ATARDECER.verde}`,
    outlineOffset: 3,
  },
} as const;

/** Botón con borde, para las otras formas de entrar. */
export function BotonSecundario({
  testid,
  onClick,
  className,
  children,
}: {
  testid: string;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={cx(
        css([
          estiloBoton,
          {
            marginTop: 10,
            background: 'transparent',
            color: ATARDECER.tinta,
            boxShadow: `inset 0 0 0 1.5px ${ATARDECER.linea}`,
          },
        ]),
        className,
      )}
    >
      {children}
    </button>
  );
}

/** La G de Google con sus cuatro colores oficiales. */
function LogoGoogle() {
  return (
    <svg viewBox="0 0 48 48" width={20} height={20} aria-hidden>
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

const sube = keyframes({
  from: { opacity: 0, transform: 'translateY(14px)' },
  to: { opacity: 1, transform: 'none' },
});

export function entraEstilo(retraso: string, duracion: string) {
  return {
    animation: `${sube} ${duracion} ease-out ${retraso} both`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  };
}

export function entra(retraso: string, duracion: string) {
  return css(entraEstilo(retraso, duracion));
}
